import React, { MouseEvent, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { Subtitle, Text, Title } from '@siftscience/focus-components/text'
import { Edit } from '@siftscience/focus-components/icons/Edit'
import { TextInput } from '@siftscience/focus-components/input'
import { Switch } from '@siftscience/focus-components/switch'
import { Info } from '@siftscience/focus-components/icons'
import { Select } from '@siftscience/focus-components/select'
import { TooltipTrigger } from '@siftscience/focus-components/tooltip'
import { Checkbox } from '@siftscience/focus-components/checkbox'
import capitalize from 'lodash/capitalize'
import { DataField, DataFieldType } from '../../dto/template'
import DatePicker from '../../../DET-edit-additions/date-picker'
import ImageUploader from '../../../DET-edit-additions/image-uploader/image-uploader'
import { useMerchantContext } from '../../context/merchant-context'
import { ImageDTO } from '../../../DET-edit-additions/dtos'
import { setLibraryEvidence } from '../../../../utils/det'

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '276px'
  },
  mainBody: {
    padding: '16px',
    borderBottom: '1px solid #E0E0E0'
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '56px',
    padding: '12px 16px'
  },
  separator: {
    width: '100%',
    height: '1px',
    backgroundColor: '#E0E0E0',
    marginBottom: '8px'
  },
  pathWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  isRequired: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px'
  },
  globalSettingsTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: '4px',
    marginBottom: '4px'
  },
  block: {
    marginBottom: '8px'
  },
  info: {
    width: 'auto',
    height: 'auto',
    borderRadius: '50%'
  },
  title: {
    marginBottom: '4px'
  },
  input: {
    width: '100%'
  },
  select: {
    width: '100%',

    '& button': {
      width: '100%'
    }
  },
  fieldWrapper: {
    margin: '4px 0'
  },
  checkboxWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  settingBtn: {
    marginTop: '12px'
  }
}))

const DATA_FIELD_OPTIONS: DataFieldType[] = Object.values(DataFieldType)
const IMAGE_PATH_PREFIX = '$.evidences'

interface ActionProps {
  defaultDataType?: DataFieldType
  dataFieldOptions?: DataFieldType[]
  defaultDataField?: Partial<DataField>
  onCancel: (event: MouseEvent) => void
  onSave: (dataField: DataField, event?: MouseEvent) => void
  onSaveDataField?: (dataField: DataField) => void
  setOpenConfigurationModal: (opened: boolean) => void
  isNewDataField: boolean
}

const Action = ({
  defaultDataType,
  dataFieldOptions = DATA_FIELD_OPTIONS,
  defaultDataField,
  onCancel,
  onSave,
  setOpenConfigurationModal,
  isNewDataField,
  onSaveDataField
}: ActionProps) => {
  const { selectedMerchant } = useMerchantContext()
  const [dataType, setDataType] = useState<DataFieldType>(
    defaultDataField?.data_type || defaultDataType
  )
  const [pathValue, setPathValue] = useState(
    defaultDataField?.dataset_path || ''
  )
  const [isPathEditable, setIsPathEditable] = useState(false)
  const [displayNameValue, setDisplayNameValue] = useState(
    defaultDataField?.name || ''
  )
  const [defaultValue, setDefaultValue] = useState(
    defaultDataField?.default_value || ''
  )
  const [merchantEvidenceId, setMerchantEvidenceId] = useState(
    defaultDataField?.merchant_evidence_id || ''
  )
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(
    defaultDataField?.default_value === 'true'
  )
  const [isRequired, setIsRequired] = useState(
    defaultDataField?.required || false
  )
  const classes = useStyles()

  const onPathChange = event => {
    const value = event.target.value
    setPathValue(value)
  }

  const buildAutoPath = (name: string): string => {
    const pathName = name.toLocaleLowerCase().split(' ').join('_')
    return dataType === DataFieldType.IMAGE
      ? `${IMAGE_PATH_PREFIX}.${pathName}`
      : `$.${pathName}`
  }

  const onDefaultNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value
    if (!defaultDataField?.datasetPath && !defaultDataField?.dataset_path) {
      setPathValue(buildAutoPath(name))
    }
    setDisplayNameValue(name)
  }

  const onDefaultValueChange = event => {
    const value = event.target.value
    setDefaultValue(value)
  }

  const onCheckboxChange = event => {
    const value = event.target.checked
    if (value === true) {
      setIsCheckboxChecked(true)
    } else {
      setIsCheckboxChecked(false)
    }
  }

  // TODO tooltipContent needs data from BE
  const tooltipContent = `This data field is used in 3 other templates.
  \n Changing the data type here will update it in:
   \n • Return Policy \n • Subscription Items \n • Billing Address`

  const onSaveAction = (event: MouseEvent) => {
    const data = {
      name: displayNameValue,
      data_type: dataType,
      required: isRequired,
      dataset_path: pathValue,
      merchant_evidence_id: merchantEvidenceId,
      default_value:
        dataType === DataFieldType.BOOLEAN
          ? String(isCheckboxChecked)
          : defaultValue
    }
    onSave(data, event)
  }

  const showConfigurationModal = () => {
    setOpenConfigurationModal(true)
    if (isNewDataField) {
      onSaveDataField({
        name: displayNameValue,
        data_type: dataType,
        required: isRequired,
        dataset_path: pathValue,
        default_value:
          dataType === DataFieldType.BOOLEAN
            ? String(isCheckboxChecked)
            : defaultValue
      })
    }
  }

  const onUploadImage = async (field: DataField, imageInfo: ImageDTO) => {
    // TODO do we really need this part?
    console.log('field', field)
    console.log('imageInfo', imageInfo)
  }

  const onUnattachImageLibraryImage = async (
    field: DataField,
    imageInfo: ImageDTO
  ) => {
    setMerchantEvidenceId(null)
  }

  const onAddImageLibraryImage = async (path: string, imageInfo: ImageDTO) => {
    setMerchantEvidenceId(imageInfo.imageLibraryData.evidence_id)
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.mainBody}>
        {dataType === DataFieldType.IMAGE && !!defaultDataField ? (
          <>
            <div className={classes.block}>
              <ImageUploader
                isWorkspace={false}
                changes={{}}
                dataField={defaultDataField}
                merchantEvidenceId={merchantEvidenceId}
                dataset={{}}
                merchantId={selectedMerchant.id}
                onUploadImage={onUploadImage}
                onDeleteImage={onUnattachImageLibraryImage}
                onAddImageLibraryImage={onAddImageLibraryImage}
              />
            </div>
            <div className={classes.separator} />
          </>
        ) : null}
        <div className={classes.block}>
          <Title size="xsmall" color="secondary" className={classes.title}>
            Display Name
          </Title>
          <TextInput
            value={displayNameValue}
            onChange={onDefaultNameChange}
            className={classes.input}
          />
          <div className={classes.isRequired}>
            <Switch
              key={`is-required-${isRequired}`}
              checked={isRequired}
              onChange={() => setIsRequired(!isRequired)}
            />
            <Text size="small">Is Required</Text>
          </div>
        </div>
        <div className={classes.separator} />
        <div className={classes.block}>
          <Title size="xsmall" color="secondary" className={classes.title}>
            Path
          </Title>
          <div className={classes.pathWrapper}>
            {isPathEditable ? (
              <TextInput
                value={pathValue}
                onChange={onPathChange}
                className={classes.input}
              />
            ) : (
              <>
                <Text size="small">{pathValue}</Text>
                <IconButton
                  onClick={() => setIsPathEditable(true)}
                  variant="secondary-ghost"
                >
                  <Edit />
                </IconButton>
              </>
            )}
          </div>
        </div>
        <div className={classes.separator} />
        <div>
          <div className={classes.globalSettingsTitle}>
            <Subtitle color="secondary">GLOBAL DATA FIELD SETTINGS</Subtitle>
            <TooltipTrigger content={tooltipContent} placement="top">
              <IconButton
                className={classes.info}
                variant="secondary-ghost"
                size="medium"
              >
                <Info color="#B3B3B3" />
              </IconButton>
            </TooltipTrigger>
          </div>
          <div className={classes.fieldWrapper}>
            <Title size="xsmall" color="secondary" className={classes.title}>
              Data Type
            </Title>
            <Select
              className={classes.select}
              variant="secondary"
              items={dataFieldOptions}
              getItemLabel={item => capitalize(item as string)}
              onChange={item => setDataType(item)}
              selectedItem={dataType}
              stretch
            />
          </div>
          {!!dataType &&
            !isNewDataField &&
            dataType === DataFieldType.ARRAY && (
              <Button
                className={classes.settingBtn}
                onClick={showConfigurationModal}
                variant="primary"
              >
                Settings
              </Button>
            )}
          {!!dataType &&
            dataType !== DataFieldType.IMAGE &&
            dataType !== DataFieldType.ARRAY && (
              <div className={classes.fieldWrapper}>
                <Title
                  size="xsmall"
                  color="secondary"
                  className={classes.title}
                >
                  Default Value
                </Title>
                {dataType === DataFieldType.TEXT && (
                  <TextInput
                    className={classes.select}
                    value={defaultValue}
                    onChange={onDefaultValueChange}
                    placeholder="No default value"
                  />
                )}
                {dataType === DataFieldType.BOOLEAN && (
                  <div className={classes.checkboxWrapper}>
                    <Checkbox
                      onChange={onCheckboxChange}
                      defaultChecked={isCheckboxChecked}
                    />
                  </div>
                )}
                {dataType === DataFieldType.DATE && (
                  <DatePicker
                    value={defaultValue as string}
                    onChange={onDefaultValueChange}
                  />
                )}
                {dataType === DataFieldType.DATETIME && (
                  <DatePicker
                    value={defaultValue as string}
                    type="datetime-local"
                    onChange={onDefaultValueChange}
                  />
                )}
              </div>
            )}
        </div>
      </div>
      <div className={classes.actions}>
        <Button variant="secondary" lined onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSaveAction}>
          Save
        </Button>
      </div>
    </div>
  )
}

export default Action
